import { useEffect, useRef } from 'react'
import { useBackground } from '../contexts/BackgroundContext'

function BackgroundLoader() {
  const { selectedBackground } = useBackground()
  const styleRef = useRef(null)
  const containerRef = useRef(null)
  const videoRef = useRef(null)
  const scriptRef = useRef(null)
  const visibilityHandlerRef = useRef(null)

  // Optimisation : Pauser les vidéos quand l'onglet n'est pas visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Onglet caché : pauser toutes les vidéos
        const container = document.getElementById('custom-background-container')
        if (container) {
          const videos = container.querySelectorAll('video')
          videos.forEach(video => {
            if (!video.paused) {
              video.dataset.wasPlaying = 'true'
              video.pause()
            }
          })
        }
      } else {
        // Onglet visible : reprendre les vidéos qui étaient en lecture
        const container = document.getElementById('custom-background-container')
        if (container) {
          const videos = container.querySelectorAll('video')
          videos.forEach(video => {
            if (video.dataset.wasPlaying === 'true') {
              video.play().catch(err => console.error('Erreur reprise vidéo:', err))
              delete video.dataset.wasPlaying
            }
          })
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    visibilityHandlerRef.current = handleVisibilityChange

    return () => {
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    console.log('BackgroundLoader: selectedBackground changé vers', selectedBackground)
    
    // Supprimer l'ancien style et conteneur
    if (styleRef.current) {
      styleRef.current.remove()
      styleRef.current = null
    }
    if (containerRef.current) {
      containerRef.current.remove()
      containerRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.remove()
      videoRef.current = null
    }
    if (scriptRef.current) {
      scriptRef.current.remove()
      scriptRef.current = null
    }
    
    // Nettoyer les variables globales du script day-sunset-night si elles existent
    if (typeof window !== 'undefined') {
      // Arrêter les intervalles précédents
      if (window.daySunsetNightInterval) {
        clearInterval(window.daySunsetNightInterval)
        window.daySunsetNightInterval = null
      }
    }

    if (selectedBackground === 'default') {
      // Utiliser le fond par défaut (déjà géré par CSS)
      console.log('BackgroundLoader: utilisation du fond par défaut')
      return
    }
    
    console.log('BackgroundLoader: chargement du background', selectedBackground)

    // Créer un conteneur pour le background
    const container = document.createElement('div')
    container.id = 'custom-background-container'
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: -2;
      pointer-events: none;
      overflow: hidden;
      will-change: transform;
      transform: translateZ(0);
    `
    document.body.appendChild(container)
    containerRef.current = container

    // Mapping des backgrounds vers leurs fichiers
    // Support pour différents types : html/css/js, video, ou project.json (Wallpaper Engine)
    const backgroundMap = {
      'bubble-float': {
        type: 'html',
        folder: 'Bubble Float',
        css: 'Bubble Float.compiled.css',
        html: 'Bubble Float.html'
      },
      'shooting-stars': {
        type: 'html',
        folder: 'Shooting stars',
        css: 'Shooting stars.css',
        html: 'Shooting stars.html'
      },
      'gradient-waves': {
        type: 'html',
        folder: 'Gradient background with waves',
        css: 'Gradient background with waves.css',
        html: 'Gradient background with waves.html'
      },
      'foil-stamping': {
        type: 'html',
        folder: 'Foil Stamping Effect',
        css: 'Foil Stamping Effect.fixed.css',
        html: 'Foil Stamping Effect.fixed.html'
      },
      'flashlight-pattern': {
        type: 'html',
        folder: 'Animated Flashlight Pattern Backgroundexternal',
        css: 'Animated Flashlight Pattern Backgroundexternal.css',
        html: 'Animated Flashlight Pattern Backgroundexternal.html'
      },
      'day-sunset-night': {
        type: 'html',
        folder: 'Day-Sunset-Night Cycle',
        css: 'css.css',
        html: 'index.html',
        js: 'script.js'
      },
      'windows-11-colorful': {
        type: 'image',
        folder: 'Windows Wallpapers',
        image: 'windows-11-stock-official-colorful-3840x2160-5658.jpg'
      },
      'windows-11-dark-abstract': {
        type: 'image',
        folder: 'Windows Wallpapers',
        image: 'windows-11-dark-mode-abstract-background-black-background-3840x2160-8710.jpg'
      },
      'anime-town-rainfall': {
        type: 'video',
        folder: 'Windows Wallpapers',
        video: 'anime-town-rainfall.1920x1080.mp4'
      },
      'backyard': {
        type: 'video',
        folder: 'Windows Wallpapers',
        video: 'backyard.1920x1080.mp4'
      },
      'lofi-coffee-shop': {
        type: 'video',
        folder: 'Windows Wallpapers',
        video: 'lofi-coffee-shop.1920x1080.mp4'
      }
    }

    const bgConfig = backgroundMap[selectedBackground]
    if (!bgConfig) {
      // Essayer de charger depuis un dossier Wallpaper Engine
      loadWallpaperEngineBackground(selectedBackground, container)
      return
    }

    // Charger selon le type
    if (bgConfig.type === 'video') {
      loadVideoBackground(bgConfig, container)
    } else if (bgConfig.type === 'image') {
      loadImageBackground(bgConfig, container)
    } else if (bgConfig.type === 'html') {
      loadHtmlBackground(bgConfig, container, selectedBackground)
    } else if (bgConfig.type === 'wallpaper-engine') {
      loadWallpaperEngineBackground(selectedBackground, container)
    }

    return () => {
      if (styleRef.current) {
        styleRef.current.remove()
        styleRef.current = null
      }
      if (containerRef.current) {
        containerRef.current.remove()
        containerRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.remove()
        videoRef.current = null
      }
      if (scriptRef.current) {
        scriptRef.current.remove()
        scriptRef.current = null
      }
      
      // Nettoyer les variables globales du script day-sunset-night
      if (typeof window !== 'undefined') {
        if (window.daySunsetNightInterval) {
          clearInterval(window.daySunsetNightInterval)
          window.daySunsetNightInterval = null
        }
        window.daySunsetNightManualLoad = false
      }
    }
  }, [selectedBackground])

  // Fonction pour charger un background image (statique)
  const loadImageBackground = (bgConfig, container) => {
    const img = document.createElement('img')
    img.src = `/backgrounds/${bgConfig.folder}/${bgConfig.image}`
    
    // Pour les GIFs, utiliser un meilleur algorithme de rendu
    if (bgConfig.image.endsWith('.gif')) {
      img.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        z-index: -2;
        pointer-events: none;
        image-rendering: auto;
        image-rendering: -webkit-optimize-contrast;
        image-rendering: smooth;
        -ms-interpolation-mode: bicubic;
        transform: translateZ(0);
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        will-change: transform;
      `
    } else {
      img.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        z-index: -2;
        pointer-events: none;
        image-rendering: -webkit-optimize-contrast;
        image-rendering: crisp-edges;
        image-rendering: high-quality;
        -ms-interpolation-mode: bicubic;
      `
    }
    
    img.onerror = () => console.error('Erreur chargement image background:', img.src)
    container.appendChild(img)
    console.log('Background image chargé:', img.src)
  }

  // Fonction pour charger un background vidéo
  const loadVideoBackground = (bgConfig, container) => {
    const video = document.createElement('video')
    video.autoplay = true
    video.loop = true
    video.muted = true
    video.playsInline = true
    video.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      min-width: 100%;
      min-height: 100%;
      width: auto;
      height: auto;
      object-fit: cover;
    `
    
    // Essayer différents formats
    const videoFormats = Array.isArray(bgConfig.video) ? bgConfig.video : [bgConfig.video || 'video.mp4']
    videoFormats.forEach(format => {
      const source = document.createElement('source')
      source.src = `/backgrounds/${bgConfig.folder}/${format}`
      source.type = `video/${format.split('.').pop()}`
      video.appendChild(source)
    })
    
    video.onerror = () => console.error('Erreur chargement vidéo background')
    container.appendChild(video)
    videoRef.current = video
    video.play().catch(err => console.error('Erreur lecture vidéo:', err))
  }

  // Fonction pour charger un background HTML/CSS/JS
  const loadHtmlBackground = (bgConfig, container, backgroundId) => {
    // Fonction pour charger le HTML et le JS
    const loadHTMLAndJS = () => {
      if (!bgConfig.html) {
        // Si pas de HTML mais du JS, charger directement
        if (bgConfig.js) {
          loadJS()
        }
        return Promise.resolve()
      }
      
      const htmlPath = `/backgrounds/${bgConfig.folder}/${bgConfig.html}`
      return fetch(htmlPath)
        .then(response => response.text())
        .then(htmlText => {
          // Extraire le contenu du body ou utiliser directement
          const parser = new DOMParser()
          const doc = parser.parseFromString(htmlText, 'text/html')
          
          // Extraire le CSS du <style> dans <head> et l'ajouter
          const styleTags = doc.querySelectorAll('head style')
          styleTags.forEach(styleTag => {
            const style = document.createElement('style')
            let cssText = styleTag.textContent
            // Adapter le CSS pour utiliser #custom-background-container
            cssText = cssText.replace(/^body\s*{/gm, '#custom-background-container {')
            cssText = cssText.replace(/\sbody\s*{/g, ' #custom-background-container {')
            // S'assurer que le conteneur prend toute la place
            if (!cssText.includes('#custom-background-container {')) {
              cssText = `#custom-background-container { position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: -2; pointer-events: none; overflow: hidden; }\n${cssText}`
            }
            style.textContent = cssText
            style.id = 'custom-background-style'
            document.head.appendChild(style)
            if (!styleRef.current) styleRef.current = style
          })
          
          const bodyContent = doc.body.innerHTML || htmlText
          container.innerHTML = bodyContent
          
          // Charger le JS après que le HTML soit injecté
          if (bgConfig.js) {
            loadJS()
          }
        })
        .catch(err => console.error('Erreur chargement HTML background:', err))
    }
    
    // Fonction pour charger le JS
    const loadJS = () => {
      if (!bgConfig.js) return
      
      const jsPath = `/backgrounds/${bgConfig.folder}/${bgConfig.js}`
      
      // Pour day-sunset-night, traitement spécial
      if (backgroundId === 'day-sunset-night') {
        fetch(jsPath)
          .then(response => response.text())
          .then(jsText => {
            // S'assurer que l'élément existe avant d'exécuter le script
            const bgImage = container.querySelector('#background')
            if (!bgImage) {
              console.error('Élément #background non trouvé dans le conteneur')
              return
            }
            
            // Adapter le script pour qu'il cherche l'élément dans le conteneur
            // D'abord, faire les remplacements dans le script original
            let adaptedJs = jsText
              // Remplacer var image = document.getElementById('background') par un placeholder temporaire
              .replace(
                /var image = document\.getElementById\('background'\);/g,
                `var image = __BG_IMAGE_PLACEHOLDER__;`
              )
              // Remplacer les autres occurrences de document.getElementById('background') par un placeholder
              .replace(
                /document\.getElementById\('background'\)/g,
                `__BG_IMAGE_PLACEHOLDER__`
              )
            
            // Maintenant, créer le helperCode qui définit la fonction (sans utiliser document.getElementById('background') directement)
            const helperCode = `(function() {
              var container = document.getElementById('custom-background-container');
              var bgImageElement = null;
              if (container) {
                bgImageElement = container.querySelector('#background');
              }
              if (!bgImageElement) {
                // Utiliser une construction différente pour éviter le remplacement
                var getById = document['getElementById'];
                bgImageElement = getById.call(document, 'background');
              }
              // Exposer une fonction simple qui retourne l'élément
              window.__bgImage = function() { 
                if (!bgImageElement) {
                  var c = document.getElementById('custom-background-container');
                  if (c) bgImageElement = c.querySelector('#background');
                  if (!bgImageElement) {
                    var getById = document['getElementById'];
                    bgImageElement = getById.call(document, 'background');
                  }
                }
                return bgImageElement;
              };
            })();`
            
            // Ajouter le helperCode au début et remplacer le placeholder
            adaptedJs = adaptedJs
              .replace(
                /(var states = \["day", "sunset", "night"\];)/,
                `${helperCode}\n$1`
              )
              // Remplacer le placeholder par l'appel à la fonction
              .replace(
                /__BG_IMAGE_PLACEHOLDER__/g,
                `(window.__bgImage && window.__bgImage()) || null`
              )
              // Ajouter une vérification pour var image = ...
              .replace(
                /var image = \(window\.__bgImage && window\.__bgImage\(\)\) \|\| null;/g,
                `var image = (window.__bgImage && window.__bgImage()) || null;\n    if (!image) { console.warn('Cannot change background: image element not found'); return; }`
              )
              // S'assurer que imageDirectory utilise le bon chemin
              .replace(
                /var imageDirectory = ".*?"/g,
                'var imageDirectory = "/backgrounds/Day-Sunset-Night Cycle/default/"'
              )
            
            // Marquer que le chargement est manuel
            if (typeof window !== 'undefined') {
              window.daySunsetNightManualLoad = true
            }
            
            // Exécuter le script adapté directement avec eval pour qu'il soit dans le scope global
            try {
              // Wrapper le script pour que toutes les variables et fonctions soient dans window
              const wrappedJs = `
                (function() {
                  ${adaptedJs}
                  // Exposer les fonctions importantes dans window avec protection
                  if (typeof load !== 'undefined') window.load = load;
                  if (typeof change !== 'undefined') {
                    var originalChange = change;
                    window.change = function(state) {
                      var img = window.__bgImage ? window.__bgImage() : null;
                      if (img) {
                        return originalChange(state);
                      } else {
                        console.warn('Cannot change background: image element not found');
                      }
                    };
                  }
                  if (typeof changeTime !== 'undefined') window.changeTime = changeTime;
                  if (typeof update !== 'undefined') {
                    var originalUpdate = update;
                    window.update = function() {
                      var img = window.__bgImage ? window.__bgImage() : null;
                      if (img) {
                        return originalUpdate();
                      } else {
                        console.warn('Cannot update background: image element not found');
                      }
                    };
                  }
                })();
              `
              // Créer une fonction qui exécute le script dans le scope global
              const executeScript = new Function(wrappedJs)
              executeScript()
              console.log('Script day-sunset-night exécuté')
              
              // Forcer l'exécution de load() après un court délai
              setTimeout(() => {
                const bgImage = container.querySelector('#background')
                if (!bgImage) {
                  console.error('Élément #background toujours non trouvé après délai')
                  return
                }
                console.log('Élément #background trouvé:', bgImage)
                
                // Vérifier si load est disponible dans window
                if (typeof window.load === 'function') {
                  console.log('Exécution de window.load()...')
                  window.load()
                } else if (typeof load === 'function') {
                  console.log('Exécution de load()...')
                  load()
                } else {
                  console.error('Fonction load() non trouvée. Variables disponibles:', Object.keys(window).filter(k => typeof window[k] === 'function').slice(0, 10))
                  // Essayer d'exécuter manuellement la logique
                  if (typeof window.changeTime === 'function' && typeof window.change === 'function') {
                    console.log('Exécution manuelle de la logique...')
                    window.changeTime()
                    const curTime = new Date()
                    const time = (curTime.getHours() * 100) + curTime.getMinutes()
                    const day_time = (7*100) + 0
                    const sunset_time = (18 * 100) + 30
                    const night_time = (20 * 100) + 0
                    
                    if (time >= day_time && time < sunset_time) window.change(0)
                    else if (time >= sunset_time && time < night_time) window.change(1)
                    else if (time >= night_time || time < day_time) window.change(2)
                  }
                }
                
                // Vérifier que l'image a bien un src après load()
                setTimeout(() => {
                  const img = container.querySelector('#background')
                  if (img) {
                    console.log('Image src après load():', img.src)
                    console.log('Image complete:', img.complete)
                    console.log('Image naturalWidth:', img.naturalWidth)
                    // Ne corriger que si l'image n'a pas de src valide OU si elle n'a pas chargé correctement
                    // (naturalWidth === 0 signifie que l'image n'a pas chargé)
                    if (!img.src || (!img.complete && img.naturalWidth === 0)) {
                      console.error('Image src incorrect ou non chargée, tentative de correction manuelle...')
                      // Forcer manuellement le src
                      const curTime = new Date()
                      const time = (curTime.getHours() * 100) + curTime.getMinutes()
                      const day_time = (7*100) + 0
                      const sunset_time = (18 * 100) + 30
                      const night_time = (20 * 100) + 0
                      
                      let imageName = 'night.jpg'
                      if (time >= day_time && time < sunset_time) imageName = 'day.jpg'
                      else if (time >= sunset_time && time < night_time) imageName = 'sunset.jpg'
                      
                      img.src = `/backgrounds/Day-Sunset-Night Cycle/default/${imageName}`
                      console.log('Image src corrigé manuellement:', img.src)
                    }
                  } else {
                    console.error('Image non trouvée après load()')
                  }
                }, 500)
              }, 300)
            } catch (error) {
              console.error('Erreur lors de l\'exécution du script:', error)
              // Fallback: charger le script normalement
              const script = document.createElement('script')
              script.textContent = adaptedJs
              document.head.appendChild(script)
              scriptRef.current = script
            }
          })
          .catch(err => console.error('Erreur chargement JS background:', err))
      } else {
        // Pour les autres backgrounds, charger normalement
        fetch(jsPath)
          .then(response => response.text())
          .then(jsText => {
            // Exécuter le script dans le scope global
            const script = document.createElement('script')
            script.textContent = jsText
            document.head.appendChild(script)
            scriptRef.current = script
            console.log('Script background chargé:', backgroundId)
          })
          .catch(err => {
            console.error('Erreur chargement JS background:', err)
            // Fallback: charger via src
            const script = document.createElement('script')
            script.src = jsPath
            script.onerror = () => console.error('Erreur chargement JS background')
            setTimeout(() => {
              document.head.appendChild(script)
              scriptRef.current = script
            }, 100)
          })
      }
    }
    
    // Charger le CSS séparé si disponible
    if (bgConfig.css) {
      const cssPath = `/backgrounds/${bgConfig.folder}/${bgConfig.css}`
      fetch(cssPath)
        .then(response => response.text())
        .then(cssText => {
          const style = document.createElement('style')
          style.id = 'custom-background-style'
          let finalCss = cssText
          
          // Ajouter les imports de polices uniquement pour foil-stamping
          if (backgroundId === 'foil-stamping') {
            finalCss = `@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Symbols+2&display=swap");\n${finalCss}`
          }
          
          // S'assurer que le conteneur prend toute la place si ce n'est pas déjà défini
          if (!finalCss.includes('#custom-background-container {')) {
            finalCss = `#custom-background-container { position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: -2; pointer-events: none; overflow: hidden; }\n${finalCss}`
          }
          
          style.textContent = finalCss
          document.head.appendChild(style)
          styleRef.current = style

          // Charger le HTML et le JS après le CSS
          return loadHTMLAndJS()
        })
        .catch(err => {
          console.error('Erreur chargement CSS background:', err)
          // Charger le HTML même si le CSS échoue
          return loadHTMLAndJS()
        })
    } else {
      // Pas de CSS séparé, charger directement le HTML et le JS
      loadHTMLAndJS()
    }
    
    // Le chargement du JS est déjà géré dans loadHTMLAndJS()
  }

  // Fonction pour charger un background Wallpaper Engine
  const loadWallpaperEngineBackground = (backgroundId, container) => {
    // Essayer de charger project.json pour la configuration
    const projectPath = `/backgrounds/${backgroundId}/project.json`
    fetch(projectPath)
      .then(response => {
        if (!response.ok) throw new Error('project.json non trouvé')
        return response.json()
      })
      .then(project => {
        // Wallpaper Engine project.json contient les infos sur le type de background
        if (project.general && project.general.type === 'video') {
          // Background vidéo
          const videoFile = project.file || 'scene.mp4'
          const video = document.createElement('video')
          video.autoplay = true
          video.loop = true
          video.muted = true
          video.playsInline = true
          video.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            min-width: 100%;
            min-height: 100%;
            width: auto;
            height: auto;
            object-fit: cover;
          `
          video.src = `/backgrounds/${backgroundId}/${videoFile}`
          video.onerror = () => console.error('Erreur chargement vidéo Wallpaper Engine')
          container.appendChild(video)
          videoRef.current = video
          video.play().catch(err => console.error('Erreur lecture vidéo:', err))
        } else {
          // Background HTML/CSS/JS - charger les fichiers principaux
          const mainFile = project.general?.file || 'index.html'
          const htmlPath = `/backgrounds/${backgroundId}/${mainFile}`
          
          fetch(htmlPath)
            .then(response => response.text())
            .then(htmlText => {
              const parser = new DOMParser()
              const doc = parser.parseFromString(htmlText, 'text/html')
              
              // Extraire et charger les CSS
              const styleLinks = doc.querySelectorAll('link[rel="stylesheet"]')
              styleLinks.forEach(link => {
                const href = link.getAttribute('href')
                if (href) {
                  const linkElement = document.createElement('link')
                  linkElement.rel = 'stylesheet'
                  linkElement.href = `/backgrounds/${backgroundId}/${href}`
                  document.head.appendChild(linkElement)
                }
              })
              
              // Extraire le contenu du body
              const bodyContent = doc.body.innerHTML || htmlText
              container.innerHTML = bodyContent
              
              // Charger les scripts
              const scripts = doc.querySelectorAll('script[src]')
              scripts.forEach(scriptTag => {
                const src = scriptTag.getAttribute('src')
                if (src) {
                  const script = document.createElement('script')
                  script.src = `/backgrounds/${backgroundId}/${src}`
                  script.onerror = () => console.error('Erreur chargement JS Wallpaper Engine')
                  setTimeout(() => {
                    document.head.appendChild(script)
                  }, 100)
                }
              })
              
              // Exécuter les scripts inline
              const inlineScripts = doc.querySelectorAll('script:not([src])')
              inlineScripts.forEach(scriptTag => {
                const script = document.createElement('script')
                script.textContent = scriptTag.textContent
                setTimeout(() => {
                  document.head.appendChild(script)
                }, 100)
              })
            })
            .catch(err => {
              console.error('Erreur chargement HTML Wallpaper Engine:', err)
              // Essayer de charger directement les fichiers CSS/JS si HTML échoue
              loadWallpaperEngineFiles(backgroundId, container)
            })
        }
      })
      .catch(() => {
        // Pas de project.json, essayer de charger directement les fichiers
        loadWallpaperEngineFiles(backgroundId, container)
      })
  }

  // Fonction pour charger les fichiers d'un background Wallpaper Engine sans project.json
  const loadWallpaperEngineFiles = (backgroundId, container) => {
    // Essayer de trouver index.html, scene.html, ou project.html
    const possibleHtmlFiles = ['index.html', 'scene.html', 'project.html', 'main.html']
    let htmlLoaded = false
    
    possibleHtmlFiles.forEach(htmlFile => {
      if (htmlLoaded) return
      
      const htmlPath = `/backgrounds/${backgroundId}/${htmlFile}`
      fetch(htmlPath)
        .then(response => {
          if (!response.ok) throw new Error('Fichier non trouvé')
          return response.text()
        })
        .then(htmlText => {
          if (htmlLoaded) return
          htmlLoaded = true
          
          const parser = new DOMParser()
          const doc = parser.parseFromString(htmlText, 'text/html')
          
          // Charger les CSS
          const styleLinks = doc.querySelectorAll('link[rel="stylesheet"]')
          styleLinks.forEach(link => {
            const href = link.getAttribute('href')
            if (href) {
              const linkElement = document.createElement('link')
              linkElement.rel = 'stylesheet'
              linkElement.href = `/backgrounds/${backgroundId}/${href}`
              document.head.appendChild(linkElement)
            }
          })
          
          // Injecter le contenu
          const bodyContent = doc.body.innerHTML || htmlText
          container.innerHTML = bodyContent
          
          // Charger les scripts
          const scripts = doc.querySelectorAll('script[src]')
          scripts.forEach(scriptTag => {
            const src = scriptTag.getAttribute('src')
            if (src) {
              const script = document.createElement('script')
              script.src = `/backgrounds/${backgroundId}/${src}`
              script.onerror = () => console.error('Erreur chargement JS')
              setTimeout(() => {
                document.head.appendChild(script)
              }, 100)
            }
          })
        })
        .catch(() => {
          // Continuer avec le fichier suivant
        })
    })
  }

  return null
}

export default BackgroundLoader

